import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@components/ui/card";
import { Field, FieldError, FieldLabel } from "@components/ui/field";
import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { useMemo } from "react";
import { toast } from "sonner";
import { Badge } from "@components/ui/badge";
import { SearchQuery } from "@/types/SearchQuery";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const removeUuid = (queries: SearchQuery[]) =>
  queries.map(({ uuid: _uuid, ...rest }) => rest);

type StaffProps = {
  onApplySearchQueries: (
    queries: Array<Omit<SearchQuery, "uuid"> & { uuid?: string | null }>
  ) => void;
  onCopyQueries: () => void;
};

export default function Staff({
  onApplySearchQueries,
  onCopyQueries,
}: StaffProps) {
  const { t } = useLingui();

  // Form state
  const jsonFormSchema = useMemo(() => {
    return z.object({
      json: z.string().min(1, { message: t`JSON är obligatoriskt` }),
    });
  }, [t]);

  const form = useForm<z.infer<typeof jsonFormSchema>>({
    resolver: zodResolver(jsonFormSchema),
    defaultValues: {
      json: "",
    },
  });

  // Handlers
  function handleSubmitJsonForm(values: z.infer<typeof jsonFormSchema>) {
    try {
      if (!values.json) {
        throw new Error();
      }

      const parsed = JSON.parse(values.json) as { queries: SearchQuery[] };

      if (!parsed || typeof parsed !== "object") {
        throw new Error();
      }

      const parsedSearchQueries = parsed?.queries;

      if (!parsedSearchQueries || !Array.isArray(parsedSearchQueries)) {
        throw new Error();
      }

      const queriesWithoutUuid = removeUuid(parsedSearchQueries);
      onApplySearchQueries(queriesWithoutUuid);

      toast.success(t`JSON tillämpades`);
      form.reset();
    } catch {
      toast.error(t`JSON värdet är inte godkänt formatterad`);
    }
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmitJsonForm)}>
      <Card className="border-purple-400 border-dashed relative">
        <Badge
          variant="purple"
          className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 ring-2 ring-purple-50 ring-offset-4 ring-offset-background"
        >
          <Trans>Staff</Trans>
        </Badge>

        <CardHeader>
          <CardTitle>
            <Trans>Interna verktyg</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>
              Här kan du skapa en bevakning utifrån JSON och kopiera
              bevakningens söksatser som JSON
            </Trans>
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <div className="flex flex-row items-start gap-2.5 w-full max-w-2xl">
            <Controller
              name="json"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "qb-form-staff-json";

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id} className="sr-only">
                      <Trans>JSON</Trans>
                    </FieldLabel>
                    <Input
                      id={id}
                      aria-invalid={fieldState.invalid}
                      placeholder={t`Klistra in bevakningens JSON här`}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.currentTarget.value);
                      }}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />
            <Button type="submit" variant="secondary">
              <Trans>Applicera</Trans>
            </Button>
          </div>
          <Button
            className="self-start"
            type="button"
            variant="secondary"
            onClick={onCopyQueries}
          >
            <Trans>Kopiera aktuella söksatser som JSON</Trans>
          </Button>
        </CardContent>
      </Card>
    </form>
  );
}
